import React from 'react';
import { useUrlLocalizer, useTranslator } from 'tradera/lang/translate';
import { useInView } from 'react-intersection-observer';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { Carousel } from 'tradera/components/carousel/carousel';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import { SellerCard } from './seller-card';

import type { ContentSwitchTab } from 'tradera/components/content-switch/content-switch';
import { ContentSwitch } from 'tradera/components/content-switch/content-switch';
import ApplicationErrorBoundary from 'tradera/components/application-error-boundary/application-error-boundary';

import {
    endpoints,
    getSellerSuggestionsQueryParams,
    getTrendingSellersQueryParams
} from 'tradera/state/services/discover-api';
import { useFetchFavouriteSellers } from './use-fetch-favourite-sellers';
import { trackContainerClick } from '../analytics';
import { useAppSelector } from 'tradera/state/hooks';
import { useIsTablet } from 'tradera/hooks/use-is-tablet';
import type { Language } from 'tradera/state/language/types';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import { isServer } from 'tradera/utils/nextjs';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';
import { useRTKQuery } from 'tradera/state/services/hooks/use-rtk-query';
import { selectRandomNumber } from 'tradera/state/random/selectors';

export const Sellers = () => {
    const { t } = useTranslator();
    const isLoggedIn = useIsLoggedIn();
    const shippingCountryCode = useAppSelector(
        (state) => state.shippingRegion.shippingCountry.countryCodeIso2
    ) as string;
    const userPreferredLanguage = useAppSelector(
        (state) => state.language.preferred
    ) as Language;
    const userLanguage = userPreferredLanguage.languageCodeIso2;
    const [loadingTriggerRef, isLoadingTriggerInView] = useInView({
        triggerOnce: true
    });
    const randomNumber = useAppSelector(selectRandomNumber);

    // On server, skipping or not is decided elsewhere.
    const skip = !isServer && !isLoadingTriggerInView;

    // On server, skipping or not is decided elsewhere.
    const skipNotLoggedIn =
        !isServer && (!isLoadingTriggerInView || !isLoggedIn);

    // Important to use this hook instead of the query directly, since we leverage caching behaviour
    // between this component and SellerItems
    const { data: favouriteSellersData, isLoading: isLoadingFavourites } =
        useFetchFavouriteSellers({
            skip: skipNotLoggedIn,
            loadingTriggerInView: isLoadingTriggerInView
        });

    const { data: sellerSuggestionsData, isLoading: isLoadingSuggestions } =
        useRTKQuery({
            endpoint: endpoints.getSellerSuggestions,
            queryParams: getSellerSuggestionsQueryParams(shippingCountryCode),
            skip: skipNotLoggedIn,
            isLoadingTriggerInView
        });

    const { favouriteSellers } = favouriteSellersData || {};
    const { favouriteSellerCardDtos: suggestedSellers } =
        sellerSuggestionsData || {};

    const { data: trendingSellersData, isLoading: isLoadingTrending } =
        useRTKQuery({
            endpoint: endpoints.getTrendingSellers,
            queryParams: getTrendingSellersQueryParams(
                shippingCountryCode,
                userLanguage
            ),
            skip,
            isLoadingTriggerInView
        });

    const { sellers: trendingSellers } = trendingSellersData || {};

    const isMobile = useIsMobile();
    const isTablet = useIsTablet();

    const { toLocalizedUrl } = useUrlLocalizer();

    // To indicate that the list is scrollable on mobile we show 1.5 items
    const numberCarouselCards = isMobile ? 1.5 : isTablet ? 2 : 4;

    const hasFavouriteSellers = favouriteSellers && favouriteSellers.length > 0;
    const hasSuggestedSellers = suggestedSellers && suggestedSellers.length > 0;
    const hasTrendingSellers = trendingSellers && trendingSellers.length > 0;
    const isLoadingData =
        isLoadingFavourites || isLoadingSuggestions || isLoadingTrending;

    let selectedTab = '';
    let options: ContentSwitchTab[] = [];

    const favouritesOption: ContentSwitchTab = {
        key: 'favourites',
        node: (
            <section className="mb-2 mx-md-3 mx-lg-2 mx-xl-0">
                <Carousel
                    id="sellers-favourites-carousel"
                    numberItemsToScroll={numberCarouselCards}
                    numberItemsToShow={numberCarouselCards}
                    padX>
                    <LeftButton size="md" />
                    <CarouselContent>
                        {favouriteSellers?.map((seller) => (
                            <SellerCard
                                key={seller.sellerId}
                                seller={seller}
                                isSuggestion={false}
                            />
                        ))}
                    </CarouselContent>
                    <RightButton size="md" />
                </Carousel>
            </section>
        ),
        label: t('startPage_sellers_favouriteTab')
    };

    const suggestedOption: ContentSwitchTab = {
        key: 'discover',
        node: (
            <section className="mb-2 mx-md-3 mx-lg-2 mx-xl-0">
                <Carousel
                    id="sellers-discover-carousel"
                    numberItemsToScroll={numberCarouselCards}
                    numberItemsToShow={numberCarouselCards}
                    padX>
                    <LeftButton size="md" />
                    <CarouselContent>
                        {suggestedSellers?.map((seller) => (
                            <SellerCard
                                key={seller.sellerId}
                                seller={seller}
                                isSuggestion
                            />
                        ))}
                    </CarouselContent>
                    <RightButton size="md" />
                </Carousel>
            </section>
        ),
        label: t('startPage_sellers_recommendationsTab')
    };

    const trendingOption: ContentSwitchTab = {
        key: 'trending',
        node: (
            <section className="mb-2 mx-md-3 mx-lg-2 mx-xl-0">
                <Carousel
                    id="sellers-trending-carousel"
                    numberItemsToScroll={numberCarouselCards}
                    numberItemsToShow={numberCarouselCards}
                    padX>
                    <LeftButton size="md" />
                    <CarouselContent>
                        {trendingSellers?.map((seller) => (
                            <SellerCard
                                key={seller.sellerId}
                                seller={seller}
                                isSuggestion={
                                    isLoggedIn &&
                                    favouriteSellers?.find(
                                        (favouriteSeller) =>
                                            favouriteSeller.sellerId ===
                                            seller.sellerId
                                    ) === undefined
                                }
                            />
                        ))}
                    </CarouselContent>
                    <RightButton size="md" />
                </Carousel>
            </section>
        ),
        label: t('startPage_sellers_trendingTab')
    };

    // This is a way to guarantee that we do not select the wrong tab.
    // If these are just added as they're loaded there's no guarantee which tab will be selected
    // since it will be a different number of options available when they're loading.
    if (!isLoadingData) {
        if (hasFavouriteSellers) {
            options = [favouritesOption];
        }

        if (hasSuggestedSellers) {
            options = [...options, suggestedOption];
        }

        if (hasTrendingSellers) {
            options = [...options, trendingOption];
        }

        const randomIndex = Math.round(randomNumber * (options.length - 1));
        selectedTab =
            options && options[randomIndex] && options[randomIndex].key;
    }

    return (
        // @ts-ignore: TS2201 because ApplicationErrorBoundry expects a different version of ReactNode?
        <ApplicationErrorBoundary>
            <StartPageSection ref={loadingTriggerRef}>
                {options.length === 0 ? null : (
                    <ContentSwitch
                        // If no data for favourite sellers available there's no need to show the tab selector, yet.
                        hideTabSelector={isLoadingData || options.length < 2}
                        id="sellers"
                        defaultSelected={selectedTab}
                        options={options}
                        title={
                            hasFavouriteSellers ? (
                                <StartPageSectionTitle
                                    className={
                                        !isLoadingData && options.length > 1
                                            ? 'mb-1'
                                            : ''
                                    }
                                    href={toLocalizedUrl('/my/seller-list')}
                                    inSpa={false} // Use 'inSPA' when mytradera-page can be navigated to without a hard reload
                                    noMargins
                                    onClick={() =>
                                        trackContainerClick(
                                            'favouriteSellers',
                                            'Favourite-Sellers header link'
                                        )
                                    }
                                    showAll>
                                    {t('startPage_sellers_title')}
                                </StartPageSectionTitle>
                            ) : (
                                <StartPageSectionTitle
                                    className={
                                        !isLoadingData && options.length > 1
                                            ? 'mb-1'
                                            : ''
                                    }
                                    noMargins>
                                    {t(
                                        'startPage_favouriteSellers_suggestionsTitle'
                                    )}
                                </StartPageSectionTitle>
                            )
                        }
                    />
                )}
            </StartPageSection>
        </ApplicationErrorBoundary>
    );
};
